import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketTiers = ({ tiers, onTierSelect, selectedTiers }) => {
  const [quantities, setQuantities] = useState({});

  const updateQuantity = (tierId, quantity) => {
    const newQuantities = { ...quantities, [tierId]: Math.max(0, quantity) };
    setQuantities(newQuantities);
    onTierSelect(tierId, quantity);
  };

  const getAvailableSeats = (tier) => {
    return tier?.capacity - tier?.sold;
  };

  const isAvailable = (tier) => {
    return getAvailableSeats(tier) > 0 && tier?.status === 'available';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-foreground">Select Tickets</h3>
      {tiers?.map((tier) => {
        const availableSeats = getAvailableSeats(tier);
        const currentQuantity = quantities?.[tier?.id] || 0;
        const maxQuantity = Math.min(availableSeats, 10); // Max 10 tickets per tier
        
        return (
          <div
            key={tier?.id}
            className={`p-6 border rounded-lg transition-all ${
              isAvailable(tier)
                ? 'border-border hover:border-primary/50 bg-card' :'border-border bg-muted opacity-60'
            }`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Tier Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-lg font-semibold text-foreground">{tier?.name}</h4>
                  {tier?.popular && (
                    <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                      Most Popular
                    </span>
                  )}
                  {!isAvailable(tier) && (
                    <span className="px-2 py-1 bg-error text-error-foreground text-xs font-medium rounded-full">
                      Sold Out
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-muted-foreground mb-3">{tier?.description}</p>
                
                {/* Tier Benefits */}
                {tier?.benefits && tier?.benefits?.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {tier?.benefits?.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-success" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Availability Info */}
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Icon name="Users" size={12} />
                    <span>{availableSeats} of {tier?.capacity} available</span>
                  </span>
                  {tier?.earlyBird && (
                    <span className="flex items-center space-x-1 text-warning">
                      <Icon name="Clock" size={12} />
                      <span>Early Bird</span>
                    </span>
                  )}
                </div>
              </div>
              
              {/* Price and Quantity */}
              <div className="flex flex-col lg:flex-row lg:items-center space-y-3 lg:space-y-0 lg:space-x-6">
                {/* Price */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-foreground">
                    ${tier?.price?.toFixed(2)}
                  </div>
                  {tier?.originalPrice && tier?.originalPrice > tier?.price && (
                    <div className="text-sm text-muted-foreground line-through">
                      ${tier?.originalPrice?.toFixed(2)}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    + ${tier?.fees?.toFixed(2)} fees
                  </div>
                </div>
                
                {/* Quantity Selector */}
                {isAvailable(tier) ? (
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center border border-border rounded-lg">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3 py-2 h-auto"
                        onClick={() => updateQuantity(tier?.id, currentQuantity - 1)}
                        disabled={currentQuantity <= 0}
                      >
                        <Icon name="Minus" size={14} />
                      </Button>
                      
                      <span className="px-4 py-2 text-sm font-medium min-w-[3rem] text-center">
                        {currentQuantity}
                      </span>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3 py-2 h-auto"
                        onClick={() => updateQuantity(tier?.id, currentQuantity + 1)}
                        disabled={currentQuantity >= maxQuantity}
                      >
                        <Icon name="Plus" size={14} />
                      </Button>
                    </div>
                    
                    {currentQuantity > 0 && (
                      <div className="text-sm text-muted-foreground">
                        ${((tier?.price + tier?.fees) * currentQuantity)?.toFixed(2)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-error font-medium">
                    {tier?.status === 'sold_out' ? 'Sold Out' : 'Unavailable'}
                  </div>
                )}
              </div>
            </div>
            {/* Tier-specific Notes */}
            {tier?.notes && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground flex items-start space-x-2">
                  <Icon name="Info" size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{tier?.notes}</span>
                </p>
              </div>
            )}
          </div>
        );
      })}
      {/* Tier Selection Summary */}
      {Object.values(quantities)?.some(q => q > 0) && (
        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <h4 className="font-medium text-foreground mb-2">Selected Tickets</h4>
          <div className="space-y-1">
            {tiers?.map((tier) => {
              const quantity = quantities?.[tier?.id] || 0;
              if (quantity === 0) return null;
              
              return (
                <div key={tier?.id} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {tier?.name} × {quantity}
                  </span>
                  <span className="font-medium text-foreground">
                    ${((tier?.price + tier?.fees) * quantity)?.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketTiers;